import { useEffect, useMemo, useState } from 'react';

const ESTIMATED_GENERATION_MS = 20000;
const TICK_MS = 250;

function formatElapsed(ms) {
  const totalSeconds = Math.max(0, Math.floor(ms / 1000));
  const minutes = Math.floor(totalSeconds / 60);
  const seconds = totalSeconds % 60;
  if (minutes <= 0) return `${seconds}s`;
  return `${minutes}m ${seconds}s`;
}

export default function SceneLoadingOverlay({ activity, isGenerating }) {
  const [startedAt] = useState(() => Date.now());
  const [now, setNow] = useState(() => Date.now());

  useEffect(() => {
    const timer = window.setInterval(() => {
      setNow(Date.now());
    }, TICK_MS);

    return () => window.clearInterval(timer);
  }, []);

  const elapsedMs = Math.max(0, now - startedAt);

  const progress = useMemo(() => {
    if (!isGenerating) return 6;
    const raw = 10 + (elapsedMs / ESTIMATED_GENERATION_MS) * 86;
    return Math.max(10, Math.min(96, raw));
  }, [elapsedMs, isGenerating]);

  const statusLabel = useMemo(() => {
    if (!isGenerating) return 'Connecting generator...';
    if (elapsedMs < 4000) return 'Blocking in the scene shell...';
    if (elapsedMs < 10000) return 'Painting the environment...';
    if (elapsedMs < 16000) return 'Refining lighting and depth...';
    return 'Finalizing background render...';
  }, [elapsedMs, isGenerating]);

  const secondaryLabel = useMemo(() => {
    if (!isGenerating) return 'Preparing the scene request';
    return 'Background generation in progress';
  }, [isGenerating]);

  return (
    <div
      style={{
        position: 'absolute',
        inset: 0,
        zIndex: 3,
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        justifyContent: 'center',
        pointerEvents: 'none',
        background: 'rgba(5, 8, 14, 0.34)',
        backdropFilter: 'blur(2px)',
      }}
    >
      <div style={{ fontSize: 48, marginBottom: 12 }}>{activity.emoji}</div>
      <div
        style={{
          fontFamily: "'Inter', ui-sans-serif, system-ui, sans-serif",
          fontSize: 14,
          color: 'white',
          marginBottom: 8,
        }}
      >
        {activity.name || activity.label}
      </div>
      <div
        style={{
          fontFamily: "'Inter', ui-sans-serif, system-ui, sans-serif",
          fontSize: 11,
          color: '#00ffc8',
          marginBottom: 12,
          textShadow: '0 0 18px rgba(0,255,200,0.3)',
        }}
      >
        {statusLabel}
      </div>
      <div
        style={{
          width: 240,
          maxWidth: 'calc(100vw - 48px)',
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
          marginBottom: 6,
          fontFamily: "'Inter', ui-sans-serif, system-ui, sans-serif",
          fontSize: 10,
          color: 'rgba(255,255,255,0.58)',
          letterSpacing: '0.04em',
        }}
      >
        <span>{secondaryLabel}</span>
        <span>{formatElapsed(elapsedMs)}</span>
      </div>
      <div
        style={{
          width: 240,
          maxWidth: 'calc(100vw - 48px)',
          height: 8,
          background: 'rgba(255,255,255,0.09)',
          border: '1px solid rgba(255,255,255,0.08)',
          borderRadius: 6,
          overflow: 'hidden',
          boxShadow: '0 0 24px rgba(0,0,0,0.22)',
        }}
      >
        <div
          style={{
            width: `${progress}%`,
            height: '100%',
            background: 'linear-gradient(90deg, #00ffc8 0%, #53b1fd 45%, #8b5cf6 100%)',
            borderRadius: 6,
            transition: 'width 240ms ease-out',
            boxShadow: '0 0 18px rgba(0,255,200,0.3)',
          }}
        />
      </div>
      <div
        style={{
          marginTop: 8,
          fontFamily: "'Inter', ui-sans-serif, system-ui, sans-serif",
          fontSize: 10,
          color: 'rgba(255,255,255,0.42)',
        }}
      >
        {Math.round(progress)}%
      </div>
    </div>
  );
}
